"use client";

import { Building2, ChartColumn, Settings, User } from "lucide-react";
import PowerOnPro from "@/components/icons/poweron-pro";
import Glaci from "@/components/icons/glaci";
import SparkleIcon from "@/components/icons/sparkle-icon";
import { resourceNavigation } from "@/components/resource-navigation";
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { AccountRailAvatar } from "@/components/account-rail-avatar";
import { AccountRailLayout } from "@/components/account-rail-layout";
import { NavPrimary } from "@/components/nav-primary";
import { UpdateDialogContent } from "@/components/update-dialog-content";
import type { AccountRailSnapshot } from "@/lib/account-rail-cache";

/** Last confirmed presentation, using the same layout and controls as the live rail. */
export function CachedAccountRail({
  snapshot,
  bootstrap = false,
}: {
  snapshot: AccountRailSnapshot;
  bootstrap?: boolean;
}) {
  const avatar = (path: string | null, organization: boolean) => (
    <SidebarMenu className="items-center">
      <SidebarMenuItem>
        <SidebarMenuButton
          disabled
          size="lg"
          className="flex max-h-8 w-8 shrink-0 items-center !justify-center !gap-0 overflow-hidden disabled:opacity-100 md:p-0"
        >
          <AccountRailAvatar
            src={path}
            pending
            kind={organization ? "organization" : "user"}
            bootstrap={bootstrap}
            fallback={
              organization ? (
                <Building2 className="size-4 text-muted-foreground" />
              ) : (
                <User className="size-4" />
              )
            }
          />
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
  return (
    <AccountRailLayout
      organization={avatar(snapshot.organizationLogo, true)}
      products={
        <NavPrimary
          items={[
            { title: "PowerOn Pro", url: "/poweron-pro", icon: PowerOnPro },
            { title: "Glaci", url: "/glaci", icon: Glaci },
          ]}
        />
      }
      resources={
        <NavPrimary
          items={[
            ...(snapshot.insights
              ? [{ title: "Insights", url: "/insights", icon: ChartColumn }]
              : []),
            ...resourceNavigation,
          ]}
        />
      }
      actions={
        <NavPrimary
          className="mt-auto"
          showUpdateDialog
          updateContent={<UpdateDialogContent />}
          items={[
            ...(snapshot.upgrade
              ? [
                  {
                    title: "Upgrade to Plus",
                    url: "/settings/subscriptions",
                    icon: SparkleIcon,
                    className: "upgrade-nav",
                  },
                ]
              : []),
            {
              title: "Settings",
              url: "/settings",
              icon: Settings,
              className: "settings-nav",
            },
          ]}
        />
      }
      user={avatar(snapshot.avatar, false)}
    />
  );
}
